import { Injectable, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  ExamEventBatchFrame,
  ExamEventContext,
  ExamEventEnvelope,
  ExamEventOverrides,
  ExamEventType,
  ExamServerEventEnvelope,
  ExamServerFrame,
} from '../models/exam-event.model';
import { StorageService } from './storage.service';

const AUTH_TOKEN_KEY = 'authToken';
/**
 * Remembers which attempt had an open event session, and whether the page
 * was closed deliberately (pagehide) — used to infer an unclean termination
 * (browser crash, killed process, OS shutdown) on the next session start.
 */
const SESSION_MARKER_KEY = 'examEventSessionMarker';
const MAX_QUEUE_SIZE = 200;
/** Keeps each beacon body well under the ~64KB sendBeacon limit. */
const BEACON_CHUNK_SIZE = 50;
const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 15000;
/**
 * Liveness ping while the exam is open. The backend treats a gap of more than
 * 30s between client events (with no away-signal) as idle time
 * (AttemptAnalyticsReducer.HEARTBEAT_GAP_THRESHOLD_MS), so this must stay
 * well under that.
 */
const HEARTBEAT_INTERVAL_MS = 3000;
/**
 * Frames after which the server closes the attempt's socket for good. Reconnecting
 * on our own would be rejected again (CONNECTION_REJECTED), re-open a paused
 * attempt without going through the start API (TEST_PAUSED), or take the attempt
 * back from the other window/device and loop (SESSION_REPLACED). Only a fresh
 * start-exam call may reconnect after one of these.
 */
const SESSION_STOP_FRAMES = new Set(['SESSION_REPLACED', 'TEST_PAUSED', 'CONNECTION_REJECTED']);
/** Close code the server uses with CONNECTION_REJECTED. */
const POLICY_VIOLATION_CLOSE_CODE = 1008;

interface SessionMarker {
  attemptId: string;
  state: 'active' | 'closed';
}

export interface EndSessionOptions {
  /**
   * true once the attempt is over (submitted / auto-submitted / expired):
   * reports TEST_SYNC_STARTED followed by TEST_SYNC_COMPLETED (or
   * TEST_SYNC_FAILED if events were lost), which triggers the server's
   * immediate sync finalization + derivation. Undelivered events go out via
   * the REST beacon when the socket is already closed.
   */
  finalSync?: boolean;
}

/**
 * Pushes exam-attempt telemetry (mock-test/mock-test-websocket-events 1.md) over the
 * per-attempt WebSocket at /ws/attempt/{attemptId}?token={authToken}&platform=web. `track`
 * is a safe no-op whenever there's no active attempt (i.e. outside the
 * exam flow), so call sites elsewhere in the app (interceptors, the security
 * service) don't need to guard on exam state themselves.
 */
@Injectable({ providedIn: 'root' })
export class ExamEventTrackerService {
  private readonly storageService = inject(StorageService);

  private socket: WebSocket | null = null;
  private context: ExamEventContext | null = null;
  private queue: ExamEventEnvelope[] = [];
  private sequenceNo = 0;
  private reconnectAttempts = 0;
  private reconnectTimerId: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimerId: ReturnType<typeof setInterval> | null = null;
  private shouldStayConnected = false;
  /** Opaque token from the server's SessionInfoFrame; authenticates the REST beacon. */
  private beaconToken: string | null = null;
  /** Events dropped by queue overflow — a sync that lost events reports TEST_SYNC_FAILED. */
  private droppedEventCount = 0;
  /** true between an unexpected socket close and the next successful open. */
  private isHeartbeatMissed = false;
  private activeQuestionIdValue: string | null = null;
  /** UNAUTHORIZED_SESSION_DETECTED is sent once per connection, however many signals detect it. */
  private unauthorizedSessionReported = false;
  /**
   * The last SESSION_STOP_FRAMES frame and the attempt it stopped. Kept so a
   * page that subscribes to serverEvents$ after it arrived (the question paper,
   * opened after the socket was started from the Start Test popup or the
   * instructions) can still react to it, and so beginSession doesn't reconnect.
   */
  private sessionStop: { attemptId: string; frame: ExamServerEventEnvelope } | null = null;

  /**
   * Frames pushed back by the server over the same socket (TEST_STARTED,
   * TEST_AUTO_SUBMITTED, ...). Relayed as-is — this service doesn't
   * interpret them, so new server-side event types show up here without
   * needing a change here.
   */
  private readonly serverEventSubject = new Subject<ExamServerEventEnvelope>();
  readonly serverEvents$: Observable<ExamServerEventEnvelope> =
    this.serverEventSubject.asObservable();

  constructor() {
    window.addEventListener('pagehide', () => this.markSessionClosed());
    window.addEventListener('storage', (event) => this.onStorageChange(event));
  }

  /**
   * The question on screen right now, set by the question paper. Lets
   * services that don't know about questions (e.g. content-protection
   * events from the security layer) attribute their events to it.
   */
  get activeQuestionId(): string | null {
    return this.context ? this.activeQuestionIdValue : null;
  }

  setActiveQuestionId(questionId: string | null): void {
    this.activeQuestionIdValue = questionId;
  }

  /** The TEST_PAUSED / CONNECTION_REJECTED / SESSION_REPLACED frame that stopped this attempt's socket, if any. */
  getSessionStop(attemptId: string): ExamServerEventEnvelope | null {
    return this.sessionStop?.attemptId === attemptId ? this.sessionStop.frame : null;
  }

  /** The start API accepted the attempt again (resume), so its socket may be reopened. */
  clearSessionStop(): void {
    this.sessionStop = null;
  }

  /**
   * Opens the attempt's socket and reports SESSION_STARTED — only when a new
   * connection is actually opened, because the server reads a second
   * SESSION_STARTED on the same attempt as TEST_RESUMED. Safe to call again
   * from each page of the exam flow.
   */
  beginSession(context: ExamEventContext): void {
    if (this.isConnectedTo(context.attemptId)) {
      return;
    }

    if (this.getSessionStop(context.attemptId)) {
      this.logConnection('stopped by the server, not reconnecting until restarted', context.attemptId);
      return;
    }

    const previousMarker = this.storageService.getItem<SessionMarker>(SESSION_MARKER_KEY);

    this.connect(context);

    // The previous session of this attempt never ended cleanly and the page
    // wasn't closed either — the tab/browser/device went away without
    // warning. Cause unknown, so this is the "unexpectedly" event rather
    // than APP_CRASHED / APP_FORCE_CLOSED.
    if (previousMarker?.attemptId === context.attemptId && previousMarker.state === 'active') {
      this.track('APP_TERMINATED_UNEXPECTEDLY');
    }

    this.storageService.setItem(SESSION_MARKER_KEY, {
      attemptId: context.attemptId,
      state: 'active',
    } satisfies SessionMarker);
    this.track('SESSION_STARTED');
    this.startHeartbeat();
  }

  /**
   * Reports SESSION_ENDED (plus the sync handshake when `finalSync`), delivers
   * everything still pending and closes the socket.
   */
  endSession(options: EndSessionOptions = {}): void {
    if (!this.context) {
      return;
    }

    this.track('SESSION_ENDED');

    if (options.finalSync) {
      this.track('TEST_SYNC_STARTED');
      const lastSeq = this.sequenceNo;
      this.track(this.droppedEventCount ? 'TEST_SYNC_FAILED' : 'TEST_SYNC_COMPLETED', { lastSeq });
    }

    this.storageService.removeItem(SESSION_MARKER_KEY);
    this.disconnect({ useBeaconFallback: Boolean(options.finalSync) });
  }

  connect(context: ExamEventContext): void {
    if (this.isConnectedTo(context.attemptId)) {
      return;
    }

    this.disconnect();
    this.context = context;
    this.shouldStayConnected = true;
    this.sequenceNo = 0;
    this.droppedEventCount = 0;
    this.isHeartbeatMissed = false;
    this.unauthorizedSessionReported = false;
    this.openSocket();
  }

  /**
   * @param useBeaconFallback send events the socket can't deliver through the
   *   REST beacon. Only useful once the attempt is finalized — the beacon
   *   path ingests in OFFLINE mode, which the server rejects for a live attempt.
   */
  disconnect(options: { useBeaconFallback?: boolean } = {}): void {
    this.shouldStayConnected = false;
    this.stopHeartbeat();

    // Push out anything still queued (e.g. SESSION_ENDED tracked right before
    // this call) instead of silently dropping it.
    this.flushQueue();

    if (this.context && this.queue.length && options.useBeaconFallback) {
      this.sendViaBeacon(this.queue);
      this.queue = [];
    }

    if (this.context && this.queue.length) {
      this.logConnection(
        `disconnecting with ${this.queue.length} unsent event(s) dropped`,
        this.context.attemptId,
        this.queue,
      );
    }

    if (this.reconnectTimerId) {
      clearTimeout(this.reconnectTimerId);
      this.reconnectTimerId = null;
    }

    this.socket?.close();
    this.socket = null;
    this.context = null;
    this.queue = [];
    this.reconnectAttempts = 0;
    this.beaconToken = null;
    this.activeQuestionIdValue = null;
  }

  /** true while this attempt's socket is open or reconnecting. */
  isConnectedTo(attemptId: string): boolean {
    return this.context?.attemptId === attemptId && this.shouldStayConnected;
  }

  track(
    eventType: ExamEventType,
    payload: Record<string, unknown> = {},
    overrides: ExamEventOverrides = {},
  ): void {
    if (!this.context) {
      return;
    }

    this.sequenceNo += 1;

    const envelope: ExamEventEnvelope = {
      eventId: this.generateEventId(),
      ...this.context,
      eventType,
      questionId: overrides.questionId ?? null,
      subjectId: overrides.subjectId ?? null,
      topicId: overrides.topicId ?? null,
      timestamp: new Date().toISOString(),
      sequenceNo: this.sequenceNo,
      payload,
    };

    this.send(envelope);
  }

  private send(envelope: ExamEventEnvelope): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      // Anything still queued must go first, or the server would see this
      // event ahead of older ones.
      if (this.queue.length) {
        this.queue.push(envelope);
        this.flushQueue();
      } else {
        this.transmit(envelope);
      }

      return;
    }

    // Not connected right now (still connecting, or between reconnect
    // attempts) — queue it and flush in submission order once we're back.
    this.queue.push(envelope);
    this.logConnection(
      `queued #${envelope.sequenceNo} ${envelope.eventType} (socket not open, ${this.queue.length} pending)`,
      envelope.attemptId,
    );

    if (this.queue.length > MAX_QUEUE_SIZE) {
      this.queue.shift();
      this.droppedEventCount += 1;
    }
  }

  private transmit(envelope: ExamEventEnvelope): void {
    this.socket?.send(JSON.stringify(envelope));
    this.logConnection(
      `sent #${envelope.sequenceNo} ${envelope.eventType} (eventId ${envelope.eventId})`,
      envelope.attemptId,
      envelope,
    );
  }

  /** One EventBatchFrame instead of N single frames; the server answers with one EventBatchResultFrame. */
  private transmitBatch(envelopes: ExamEventEnvelope[]): void {
    const frame: ExamEventBatchFrame = { events: envelopes };
    this.socket?.send(JSON.stringify(frame));
    this.logConnection(
      `sent batch of ${envelopes.length} (#${envelopes[0].sequenceNo}–#${envelopes[envelopes.length - 1].sequenceNo})`,
      envelopes[0].attemptId,
      envelopes.map((envelope) => envelope.eventType),
    );
  }

  /**
   * REST fallback for events the socket can no longer deliver (it was closed
   * by the server after finalization, or the network dropped). Authenticated
   * by the opaque beaconToken rather than the JWT, because sendBeacon can't
   * set an Authorization header. The server dedups on eventId, so a batch
   * that also made it over the socket is harmless.
   */
  private sendViaBeacon(envelopes: ExamEventEnvelope[]): void {
    const attemptId = envelopes[0]?.attemptId;

    if (!attemptId) {
      return;
    }

    if (!this.beaconToken) {
      this.logConnection(
        `no beacon token yet, ${envelopes.length} event(s) can't be delivered`,
        attemptId,
        envelopes,
      );
      return;
    }

    const url = `${environment.apiUrl}/mock-test/attempts/${encodeURIComponent(attemptId)}/events/beacon?token=${encodeURIComponent(this.beaconToken)}`;

    for (let start = 0; start < envelopes.length; start += BEACON_CHUNK_SIZE) {
      const frame: ExamEventBatchFrame = {
        events: envelopes.slice(start, start + BEACON_CHUNK_SIZE),
      };
      const body = JSON.stringify(frame);
      const queued =
        typeof navigator.sendBeacon === 'function' &&
        navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));

      if (!queued) {
        void fetch(url, {
          method: 'POST',
          body,
          headers: { 'Content-Type': 'application/json' },
          keepalive: true,
        }).catch(() => undefined);
      }

      this.logConnection(
        `beacon sent ${frame.events.length} event(s)`,
        attemptId,
        frame.events.map((envelope) => envelope.eventType),
      );
    }
  }

  private openSocket(): void {
    if (!this.context) {
      return;
    }

    const attemptId = this.context.attemptId;
    const token = this.storageService.getItem<string>(AUTH_TOKEN_KEY);

    // Without a token the backend rejects the handshake with 401, so don't
    // open a socket that's guaranteed to fail.
    if (!token) {
      this.logConnection('no auth token in storage, will retry', attemptId);
      this.scheduleReconnect();
      return;
    }

    const url = `${this.resolveWsBaseUrl()}/ws/attempt/${encodeURIComponent(attemptId)}?token=${encodeURIComponent(token)}&platform=web`;

    this.logConnection(`connecting (attempt ${this.reconnectAttempts + 1})…`, attemptId);

    let socket: WebSocket;

    try {
      socket = new WebSocket(url);
    } catch (error) {
      this.logConnection('failed to construct WebSocket, will retry', attemptId, error);
      this.scheduleReconnect();
      return;
    }

    this.socket = socket;

    // Every handler checks it still belongs to the current socket: close()
    // fires asynchronously, so a socket replaced by disconnect()/connect()
    // would otherwise null out (and re-open alongside) its replacement.
    socket.addEventListener('open', () => {
      if (this.socket !== socket) {
        return;
      }

      this.logConnection('open', attemptId);
      this.reconnectAttempts = 0;
      this.flushQueue();

      // Tracked after the flush so it's sequenced behind everything that
      // was queued while the connection was down.
      if (this.isHeartbeatMissed) {
        this.isHeartbeatMissed = false;
        this.track('SESSION_HEARTBEAT_RESTORED');
      }
    });

    socket.addEventListener('message', (event) => {
      if (this.socket !== socket) {
        return;
      }

      this.handleIncomingMessage(event.data, attemptId);
    });

    socket.addEventListener('close', (event) => {
      if (this.socket !== socket) {
        return;
      }

      this.logConnection(
        `closed (code ${event.code}${event.reason ? `, reason: ${event.reason}` : ''})`,
        attemptId,
      );
      this.socket = null;

      // 4000-4999 are application close codes and 1008 is CONNECTION_REJECTED:
      // the backend deliberately rejected this attempt (e.g. attempt not found,
      // another exam in progress), so retrying with the same attemptId/token
      // can't succeed.
      if (
        (event.code >= 4000 && event.code <= 4999) ||
        event.code === POLICY_VIOLATION_CLOSE_CODE
      ) {
        this.logConnection(
          `server rejected the connection (code ${event.code}), not reconnecting`,
          attemptId,
        );
        this.shouldStayConnected = false;
        return;
      }

      if (this.shouldStayConnected) {
        // The live connection dropped on its own — the client has stopped
        // delivering its activity stream until the reconnect succeeds.
        // Queued now, delivered (in order) once back.
        if (!this.isHeartbeatMissed) {
          this.isHeartbeatMissed = true;
          this.track('SESSION_HEARTBEAT_MISSED');
        }

        this.scheduleReconnect();
      }
    });

    socket.addEventListener('error', () => {
      if (this.socket !== socket) {
        return;
      }

      this.logConnection('error (close event will follow and drives reconnection)', attemptId);
    });
  }

  private handleIncomingMessage(data: unknown, attemptId: string): void {
    if (typeof data !== 'string') {
      this.logConnection('received non-text frame, ignoring', attemptId, data);
      return;
    }

    let parsed: ExamServerFrame;

    try {
      parsed = JSON.parse(data) as ExamServerFrame;
    } catch (error) {
      this.logConnection('received frame that is not valid JSON, ignoring', attemptId, {
        data,
        error,
      });
      return;
    }

    if (!parsed || typeof parsed !== 'object') {
      this.logConnection('received frame that is not an object, ignoring', attemptId, parsed);
      return;
    }

    this.captureBeaconToken(parsed);

    // Proctoring and session frames (PROCTORING_WARNING, TEST_AUTO_SUBMITTED,
    // TEST_PAUSED, ...) come as a flat `frameType` frame rather than an event
    // envelope; relay them with the whole frame as the payload so subscribers
    // read one shape.
    if (typeof parsed['eventType'] !== 'string' && typeof parsed['frameType'] === 'string') {
      const frameType = parsed['frameType'];
      const serverEvent: ExamServerEventEnvelope = { eventType: frameType, payload: parsed };
      this.logConnection(`received ${frameType}`, attemptId, parsed);

      // Reported while this socket is still open, so it can still carry it.
      if (frameType === 'SESSION_REPLACED') {
        this.reportUnauthorizedSession('session_replaced');
      }

      // The server closes the socket right after these; it must stay down.
      if (SESSION_STOP_FRAMES.has(frameType)) {
        this.shouldStayConnected = false;
        this.sessionStop = { attemptId, frame: serverEvent };
      }

      this.serverEventSubject.next(serverEvent);
      return;
    }

    if (typeof parsed['eventType'] !== 'string') {
      // SessionInfoFrame, EventBatchResultFrame, NACKs — nothing to relay.
      this.logConnection('received non-event frame', attemptId, parsed);
      return;
    }

    this.logConnection(`received ${parsed['eventType']}`, attemptId, parsed);
    this.serverEventSubject.next(parsed as ExamServerEventEnvelope);
  }

  /** SessionInfoFrame arrives right after connect; the token is reused across reconnects. */
  private captureBeaconToken(frame: ExamServerFrame): void {
    const payload = frame['payload'];
    const token =
      frame['beaconToken'] ??
      (payload && typeof payload === 'object'
        ? (payload as Record<string, unknown>)['beaconToken']
        : undefined);

    if (typeof token === 'string' && token) {
      this.beaconToken = token;
    }
  }

  /**
   * The page is going away on purpose (tab close, reload, navigating off the
   * site), so the next session start mustn't be reported as an unexpected
   * termination.
   */
  private markSessionClosed(): void {
    const marker = this.storageService.getItem<SessionMarker>(SESSION_MARKER_KEY);

    if (marker?.state === 'active') {
      this.storageService.setItem(SESSION_MARKER_KEY, {
        ...marker,
        state: 'closed',
      } satisfies SessionMarker);
    }
  }

  /**
   * The auth token only changes on login/logout, so another tab of this
   * browser signing in (as anyone) or out while the attempt is running means
   * the session the exam was started under is no longer the browser's own.
   * `key` is null when another tab cleared all of localStorage.
   */
  private onStorageChange(event: StorageEvent): void {
    if (!this.context || event.storageArea !== localStorage) {
      return;
    }

    if (event.key !== null && (event.key !== AUTH_TOKEN_KEY || event.oldValue === event.newValue)) {
      return;
    }

    const tokenNow = event.key === null ? null : event.newValue;
    this.reportUnauthorizedSession(tokenNow ? 'auth_token_changed' : 'auth_token_removed');
  }

  private reportUnauthorizedSession(
    trigger: 'session_replaced' | 'auth_token_changed' | 'auth_token_removed',
  ): void {
    if (this.unauthorizedSessionReported) {
      return;
    }

    this.unauthorizedSessionReported = true;
    this.track('UNAUTHORIZED_SESSION_DETECTED', { trigger }, { questionId: this.activeQuestionId });
  }

  private logConnection(message: string, attemptId: string, detail?: unknown): void {
    if (environment.production) {
      return;
    }

    if (detail !== undefined) {
      console.log(`[ExamEventTracker] attempt ${attemptId}: ${message}`, detail);
    } else {
      console.log(`[ExamEventTracker] attempt ${attemptId}: ${message}`);
    }
  }

  private flushQueue(): void {
    if (!this.queue.length || this.socket?.readyState !== WebSocket.OPEN) {
      return;
    }

    const pending = this.queue;
    this.queue = [];
    this.logConnection(`flushing ${pending.length} queued event(s)`, pending[0].attemptId);

    if (pending.length === 1) {
      this.transmit(pending[0]);
    } else {
      this.transmitBatch(pending);
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();

    this.heartbeatTimerId = setInterval(() => {
      // Only while the socket is live: a heartbeat queued during an outage
      // would claim the student was active through a gap the server should
      // see (SESSION_HEARTBEAT_MISSED already reports it), and would fill
      // the queue until real events get dropped.
      if (!this.context || this.socket?.readyState !== WebSocket.OPEN) {
        return;
      }

      this.track('HEARTBEAT', {}, { questionId: this.activeQuestionIdValue });
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimerId) {
      clearInterval(this.heartbeatTimerId);
      this.heartbeatTimerId = null;
    }
  }

  private scheduleReconnect(): void {
    if (!this.shouldStayConnected || this.reconnectTimerId) {
      return;
    }

    const delay = Math.min(
      RECONNECT_BASE_DELAY_MS * 2 ** this.reconnectAttempts,
      RECONNECT_MAX_DELAY_MS,
    );
    this.reconnectAttempts += 1;

    if (this.context) {
      this.logConnection(`reconnecting in ${delay}ms`, this.context.attemptId);
    }

    this.reconnectTimerId = setTimeout(() => {
      this.reconnectTimerId = null;
      this.openSocket();
    }, delay);
  }

  private resolveWsBaseUrl(): string {
    if (environment.wsUrl) {
      return environment.wsUrl;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    return `${protocol}://${window.location.host}`;
  }

  private generateEventId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    // crypto.randomUUID only exists in secure contexts (https / localhost),
    // so over plain http://<ip> build an RFC 4122 v4 UUID by hand.
    const bytes = new Uint8Array(16);

    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      crypto.getRandomValues(bytes);
    } else {
      for (let i = 0; i < bytes.length; i++) {
        bytes[i] = Math.floor(Math.random() * 256);
      }
    }

    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
}
